import { useQuery } from "@tanstack/react-query";
import { useServices } from "@/services/ServiceProvider";
import { useAuthStore } from "@/stores/useAuthStore";
import type { ChannelRow } from "../channelManifest";

export type { ChannelProvider, ChannelRow } from "../channelManifest";
export { getChannelDisplayName } from "../channelManifest";

/**
 * Shared so mutations can invalidate the same cache this query fills. Keyed by
 * the user: RLS returns the caller's own contacts, so one account's channels
 * must not be served to the next one on the same browser.
 */
export const channelsQueryKey = (userId: string | undefined) => ["user_contacts", userId] as const;

// The caller's own unrevoked contacts -- see IChannelService.listMine.
export function useNotificationChannels(options?: { pollMs?: number; enabled?: boolean }) {
  const { channels } = useServices();
  const userId = useAuthStore((s) => s.session?.userId);
  return useQuery({
    queryKey: channelsQueryKey(userId),
    queryFn: (): Promise<ChannelRow[]> => channels.listMine(),
    // Polling lets the "Connect Telegram" modal flip to Connected state as
    // soon as the webhook writes the user_contacts row. 5s keeps the loop
    // tight without hammering the DB when the page is idle.
    refetchInterval: options?.pollMs ?? false,
    // Off until there is a user (or the caller opts out, e.g. the readiness
    // gate on an admin): an anonymous fetch only ever returns [].
    enabled: (options?.enabled ?? true) && !!userId,
  });
}
